/**
 * @file apps/web/src/components/PMDashboard.tsx
 * @description Project Manager dashboard — recharts graphs + stat cards.
 * Shows pass rate trend, defect breakdown by severity, and test script status distribution.
 * Uses Supabase realtime channel for live updates when available.
 */

import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { TrendingUp, Bug, ClipboardList, CheckCircle, AlertCircle, Clock } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PMStats {
  activeProjects:     number;
  openUatSessions:    number;
  pendingApprovals:   number;
  openDefects:        number;
  passRate:           number; // 0–100
  testScriptsTotal:   number;
  testScriptsPassed:  number;
  testScriptsFailed:  number;
  testScriptsDraft:   number;
}

export interface PassRatePoint {
  label:    string;  // e.g. "Sprint 1", "Mar 10"
  pass:     number;  // 0–100
  fail:     number;
  blocked:  number;
}

export interface DefectSeverityPoint {
  severity: string;
  count:    number;
}

interface PMDashboardProps {
  stats:        PMStats;
  passRateData: PassRatePoint[];
  defectData:   DefectSeverityPoint[];
  loading?:     boolean;
  className?:   string;
}

// ---------------------------------------------------------------------------
// Design tokens (referenced from CSS vars — no hardcoded colors)
// ---------------------------------------------------------------------------

const C = {
  blue:    '#0055FF',
  sky:     '#E6EEFF',
  ink:     '#08111F',
  success: '#16A34A',
  danger:  '#DC2626',
  warning: '#D97706',
  muted:   '#6B7280',
  orange:  '#EA580C',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: C.danger,
  high:     C.orange,
  medium:   C.warning,
  low:      C.blue,
};

const SCRIPT_COLORS = [C.success, C.danger, C.blue, C.muted];

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

interface StatCardProps {
  label:    string;
  value:    string | number;
  icon:     React.ReactNode;
  trend?:   'up' | 'down' | 'neutral';
  sub?:     string;
  accentBg?: string;
  accentText?: string;
}

function StatCard({ label, value, icon, sub, accentBg = 'bg-sky', accentText = 'text-blue' }: StatCardProps) {
  return (
    <div className="card p-5 flex items-start gap-4">
      <div className={['w-10 h-10 rounded-lg flex items-center justify-center shrink-0', accentBg, accentText].join(' ')}>
        {icon}
      </div>
      <div>
        <p className="section-label">{label}</p>
        <p className="font-display text-3xl font-bold text-ink mt-0.5 leading-none">{value}</p>
        {sub && <p className="text-xs text-muted mt-1">{sub}</p>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom tooltip
// ---------------------------------------------------------------------------

function CustomTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[rgba(8,17,31,0.1)] rounded-lg shadow-modal px-3 py-2 text-xs">
      <p className="font-mono font-semibold text-ink mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="flex items-center gap-1.5" style={{ color: p.color }}>
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold">{p.value}%</span>
        </p>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1,2,3,4].map((i) => (
          <div key={i} className="card h-24 bg-gray/60" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card h-56 bg-gray/60" />
        <div className="card h-56 bg-gray/60" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PMDashboard
// ---------------------------------------------------------------------------

export function PMDashboard({
  stats,
  passRateData,
  defectData,
  loading,
  className = '',
}: PMDashboardProps) {
  if (loading) return <DashboardSkeleton />;

  const scriptDistribution = [
    { name: 'Passed',        value: stats.testScriptsPassed },
    { name: 'Failed',        value: stats.testScriptsFailed },
    { name: 'Ready to Test', value: Math.max(0, stats.testScriptsTotal - stats.testScriptsPassed - stats.testScriptsFailed - stats.testScriptsDraft) },
    { name: 'Draft',         value: stats.testScriptsDraft },
  ].filter((d) => d.value > 0);

  return (
    <div className={['space-y-6', className].join(' ')}>
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Pass Rate"
          value={`${stats.passRate.toFixed(0)}%`}
          icon={<TrendingUp size={18} />}
          sub={`${stats.testScriptsPassed} of ${stats.testScriptsTotal} scripts passed`}
          accentBg={stats.passRate >= 90 ? 'bg-success/12' : stats.passRate >= 70 ? 'bg-warning/12' : 'bg-danger/12'}
          accentText={stats.passRate >= 90 ? 'text-success' : stats.passRate >= 70 ? 'text-warning' : 'text-danger'}
        />
        <StatCard
          label="Open Defects"
          value={stats.openDefects}
          icon={<Bug size={18} />}
          sub="Active issues to resolve"
          accentBg={stats.openDefects === 0 ? 'bg-success/12' : 'bg-danger/12'}
          accentText={stats.openDefects === 0 ? 'text-success' : 'text-danger'}
        />
        <StatCard
          label="Pending Approvals"
          value={stats.pendingApprovals}
          icon={<CheckCircle size={18} />}
          sub="Awaiting decision"
        />
        <StatCard
          label="UAT Sessions"
          value={stats.openUatSessions}
          icon={<Clock size={18} />}
          sub="In progress or scheduled"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pass rate trend */}
        <div className="card p-5">
          <h3 className="font-display text-sm font-semibold text-ink mb-4">
            Pass Rate by Session
          </h3>
          {passRateData.length === 0 ? (
            <div className="empty-state py-8">
              <AlertCircle size={24} className="text-muted/40 mb-2" />
              <p className="text-xs text-muted">No session data yet.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={passRateData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(8,17,31,0.06)" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: 'DM Mono', fill: C.muted }} />
                <YAxis tick={{ fontSize: 10, fontFamily: 'DM Mono', fill: C.muted }} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconSize={8}
                  wrapperStyle={{ fontSize: '11px', fontFamily: 'DM Mono' }}
                />
                <Line type="monotone" dataKey="pass"    stroke={C.success} strokeWidth={2} dot={{ r: 3 }} name="Pass%" />
                <Line type="monotone" dataKey="fail"    stroke={C.danger}  strokeWidth={2} dot={{ r: 3 }} name="Fail%" />
                <Line type="monotone" dataKey="blocked" stroke={C.warning} strokeWidth={1.5} dot={{ r: 2 }} name="Blocked%" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Defect severity */}
        <div className="card p-5">
          <h3 className="font-display text-sm font-semibold text-ink mb-4">
            Open Defects by Severity
          </h3>
          {defectData.length === 0 ? (
            <div className="empty-state py-8">
              <Bug size={24} className="text-muted/40 mb-2" />
              <p className="text-xs text-muted">No open defects.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={defectData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(8,17,31,0.06)" />
                <XAxis dataKey="severity" tick={{ fontSize: 10, fontFamily: 'DM Mono', fill: C.muted }} />
                <YAxis tick={{ fontSize: 10, fontFamily: 'DM Mono', fill: C.muted }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    fontSize: '11px', fontFamily: 'DM Mono',
                    border: '1px solid rgba(8,17,31,0.1)',
                    borderRadius: '0.5rem', boxShadow: '0 4px 12px rgba(8,17,31,0.1)',
                  }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {defectData.map((entry) => (
                    <Cell
                      key={entry.severity}
                      fill={SEVERITY_COLORS[entry.severity] ?? C.muted}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Script status donut */}
      {scriptDistribution.length > 0 && (
        <div className="card p-5">
          <h3 className="font-display text-sm font-semibold text-ink mb-4">
            Test Script Status Distribution
          </h3>
          <div className="flex items-center gap-8">
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie
                  data={scriptDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={48}
                  outerRadius={72}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {scriptDistribution.map((_, i) => (
                    <Cell key={i} fill={SCRIPT_COLORS[i % SCRIPT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: '11px', fontFamily: 'DM Mono' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5">
              {scriptDistribution.map((d, i) => (
                <div key={d.name} className="flex items-center gap-2 text-sm">
                  <span
                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ background: SCRIPT_COLORS[i % SCRIPT_COLORS.length] }}
                  />
                  <span className="text-ink/70 font-mono text-xs">{d.name}</span>
                  <span className="font-mono text-xs font-semibold text-ink ml-auto pl-4">{d.value}</span>
                </div>
              ))}
              <div className="pt-1 border-t border-[rgba(8,17,31,0.06)] flex items-center gap-2 text-xs">
                <span className="font-mono text-muted">Total</span>
                <span className="font-mono font-bold text-ink ml-auto">{stats.testScriptsTotal}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
